import { storePath, storeProductPath } from '../lib/storePath';
import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  ShoppingCart,
  Plus,
  Minus,
  ImageOff,
  ChevronDown,
  Check,
  Heart,
  Timer,
} from "lucide-react";

import { Card, CardContent } from "./ui/card";
import { Button } from "./ui/button";

const ProductCard = ({
  product,
  vendor,
  onAddToCart,
  fromVendorStore = false,
  canFavorite = false,
  isFavorite = false,
  onToggleFavorite,
  isLoading: externalLoading = false,
}) => {
  const [adding, setAdding] = useState(false);
  const [savingFavorite, setSavingFavorite] = useState(false);
  const isLoading = externalLoading || adding;
  const store = { ...(typeof product?.vendorId === 'object' ? product.vendorId : {}), ...product?.store, ...vendor };
  const storeClosed = store.isOpen === false || store.adminClosed === true;

  // ============================================================
  // STATE
  // ============================================================
  const [quantity, setQuantity] = useState(1);
  const [showQuantityInput, setShowQuantityInput] = useState(false);
  const [showVariants, setShowVariants] = useState(false);
  const [imageError, setImageError] = useState(false);
  const [vendorLogoError, setVendorLogoError] = useState(false);
  const [toastMessage, setToastMessage] = useState("");
  const [selectedPrice, setSelectedPrice] = useState(0);

  // ============================================================
  // VARIANTS
  // ============================================================
  const variants = useMemo(() => {
    if (!Array.isArray(product?.variants)) return [];
    return product.variants
      .filter((v) => v && String(v.variantName || "").trim())
      .map((v) => ({
        ...v,
        variantName: String(v.variantName).trim(),
        originalPrice: Number(v.originalPrice) || 0,
        discountedPrice:
          v.discountedPrice !== undefined && v.discountedPrice !== null
            ? Number(v.discountedPrice)
            : null,
      }));
  }, [product?.variants]);

  const defaultVariant = useMemo(() => {
    return variants.find((v) => v.isDefault) || variants[0] || null;
  }, [variants]);

  const [selectedVariant, setSelectedVariant] = useState(defaultVariant);

  // Keep selectedVariant in sync when product changes
  useEffect(() => {
    setSelectedVariant(defaultVariant);
    setQuantity(1);
    setShowQuantityInput(false);
    setShowVariants(false);
    setImageError(false);
    setVendorLogoError(false);
  }, [product?._id, defaultVariant, vendor?.logo]);

  // ============================================================
  // PRICES
  // ============================================================
  const originalPrice = useMemo(() => {
    return selectedVariant?.originalPrice ?? 0;
  }, [selectedVariant]);

  const discountedPrice = useMemo(() => {
    const val = selectedVariant?.discountedPrice;
    return val !== undefined && val !== null && Number.isFinite(val) && val >= 0
      ? val
      : null;
  }, [selectedVariant]);

  const hasDiscount =
    discountedPrice !== null && discountedPrice < originalPrice;
  const currentPrice = hasDiscount ? discountedPrice : originalPrice;
  const discountPercentage =
    hasDiscount && originalPrice > 0
      ? Math.round(((originalPrice - discountedPrice) / originalPrice) * 100)
      : 0;

  // Update selectedPrice whenever currentPrice changes
  useEffect(() => {
    setSelectedPrice(currentPrice);
  }, [currentPrice]);

  // ============================================================
  // IMAGES, STOCK
  // ============================================================
  const images = useMemo(() => {
    return Array.isArray(product?.images) ? product.images.filter(Boolean) : [];
  }, [product?.images]);

  const imageUrl = images[0] || null;
  const discountBadges = hasDiscount && (
    <div className="absolute top-1 left-1 sm:top-3 sm:left-3">
      <span className="inline-flex items-center rounded-full bg-red-500 px-1.5 py-0.5 text-[9px] font-bold text-white shadow-md sm:px-3 sm:py-1 sm:text-xs">
        {discountPercentage}% OFF
      </span>
    </div>
  );

  const stockAvailable = useMemo(() => {
    const val = Number(selectedVariant ? selectedVariant.stockAvailable || 0 : product?.stockAvailable);
    return Number.isFinite(val) && val >= 0 ? Math.floor(val) : null;
  }, [product?.stockAvailable, selectedVariant]);

  const isOutOfStock = stockAvailable !== null && stockAvailable <= 0;
  const isProductHold = Boolean(product?.isProductHold || store.productsOnHold);
  const isUnavailable = storeClosed || isProductHold || isOutOfStock;
  const unavailableLabel = storeClosed ? 'Store closed' : isProductHold ? 'On hold' : isOutOfStock ? 'Out of stock' : '';
  const maxQuantity =
    stockAvailable !== null && stockAvailable > 0 ? stockAvailable : 99;

  // ============================================================
  // VARIANT SELECTOR HANDLERS
  // ============================================================
  const toggleVariants = () => {
    if (isLoading) return;
    if (variants.length <= 1) {
      showToast(
        variants.length === 1
          ? `Only ${variants[0].variantName} is available for this product.`
          : "No product variants are available.",
      );
      return;
    }
    setShowVariants((prev) => !prev);
  };

  const handleVariantChange = (variant) => {
    if (isLoading || !variant) return;
    setSelectedVariant(variant);
    setQuantity(1);
    setShowQuantityInput(false);
    setShowVariants(false);
  };

  // ============================================================
  // QUANTITY
  // ============================================================
  const handleIncreaseQuantity = () => {
    setQuantity((q) => Math.min(maxQuantity, q + 1));
  };

  const handleDecreaseQuantity = () => {
    setQuantity((q) => Math.max(1, q - 1));
  };

  const handleQuantityInput = (e) => {
    const val = parseInt(e.target.value, 10);
    if (Number.isNaN(val)) {
      setQuantity(1);
      return;
    }
    setQuantity(Math.min(maxQuantity, Math.max(1, val)));
  };

  // ============================================================
  // ADD TO CART – exact match with backend validation
  // ============================================================
  const handleAddToCart = async () => {
    if (storeClosed) return;
    // --------------------------------------------------
    // 0. Basic state validation
    // --------------------------------------------------
    if (isLoading || isUnavailable) {
      return;
    }

    // --------------------------------------------------
    // 1. Check authentication
    // --------------------------------------------------


    // --------------------------------------------------
    // 2. Validate selected price
    // --------------------------------------------------
    const price = Number(selectedPrice);

    if (!Number.isFinite(price) || price <= 0) {
      showToast("This product does not have a valid price.");
      return;
    }

    // --------------------------------------------------
    // 3. Validate quantity
    // --------------------------------------------------
    const cartQuantity = Number(quantity);

    if (!Number.isInteger(cartQuantity) || cartQuantity < 1) {
      showToast("Please select a valid quantity.");
      return;
    }

    // --------------------------------------------------
    // 4. Ensure a variant is selected
    // --------------------------------------------------
    let variantToUse = selectedVariant;

    if (variants.length > 0 && !variantToUse) {
      variantToUse = variants[0];
      setSelectedVariant(variantToUse);
    }

    // --------------------------------------------------
    // 5. Build product display name
    // --------------------------------------------------
    const baseProductName =
      String(product?.name || "").trim() || "Unnamed Product";

    const variantName = String(variantToUse?.variantName || "").trim();

    const displayProductName = variantName
      ? `${baseProductName} - ${variantName}`
      : baseProductName;

    // --------------------------------------------------
    // 6. Get product ID
    // --------------------------------------------------
    const productId = product?._id || product?.id;

    if (!productId) {
      showToast("Product information is incomplete. Cannot add to cart.");

      console.error("Missing product ID:", product);

      return;
    }

    // --------------------------------------------------
    // 7. Get vendor ID
    // --------------------------------------------------
    const vendorId =
      vendor?._id || vendor?.id || product?.vendorId?._id || product?.vendorId;

    if (!vendorId) {
      showToast("Vendor information is missing. Cannot add to cart.");

      console.error("Missing vendor ID:", {
        vendor,
        product,
      });

      return;
    }

    // --------------------------------------------------
    // 8. Build cart payload
    // --------------------------------------------------
    const cartPayload = {
      productId: String(productId),
      variantId: variantToUse?._id,
      variantName,
      vendorId: String(vendorId),
      name: displayProductName,
      price,
      quantity: cartQuantity,
    };

    // --------------------------------------------------
    // 9. Final payload validation
    // --------------------------------------------------
    const isValidPayload =
      Boolean(cartPayload.productId) &&
      Boolean(cartPayload.vendorId) &&
      Boolean(cartPayload.name) &&
      Number.isFinite(cartPayload.price) &&
      cartPayload.price > 0 &&
      Number.isInteger(cartPayload.quantity) &&
      cartPayload.quantity >= 1;

    if (!isValidPayload) {
      console.error("Invalid cart payload:", cartPayload);

      showToast("Invalid product information. Please try again.");

      return;
    }

    // --------------------------------------------------
    // 10. Make sure handler exists
    // --------------------------------------------------
    if (typeof onAddToCart !== "function") {
      console.error("onAddToCart is not a function");

      showToast("Add to cart is currently unavailable.");

      return;
    }

    // --------------------------------------------------
    // 11. Add to cart
    // --------------------------------------------------
    try {
      console.log("Adding product to cart:", cartPayload);

      setAdding(true);
      await onAddToCart(cartPayload);

      // ------------------------------------------------
      // 12. Reset quantity UI
      // ------------------------------------------------
      setQuantity(1);
      setShowQuantityInput(false);

      // ------------------------------------------------
      // 13. Success message
      // ------------------------------------------------
      showToast(`${displayProductName} added to your cart.`);
    } catch (error) {
      console.error("Failed to add product to cart:", error);

      showToast(error.response?.data?.message || error.message || "Unable to add the product to your cart. Please try again.");
    } finally { setAdding(false); }
  };

  // ============================================================
  // TOAST
  // ============================================================
  useEffect(() => {
    if (!toastMessage) return;
    const timer = setTimeout(() => setToastMessage(""), 2500);
    return () => clearTimeout(timer);
  }, [toastMessage]);

  const showToast = (message) => setToastMessage(message);

  const handleToggleFavorite = async () => {
    const productId = product?._id || product?.id;
    if (!onToggleFavorite || !productId) {
      showToast("Favorites are unavailable for this product.");
      return;
    }
    setSavingFavorite(true);
    try {
      await onToggleFavorite(productId);
    } catch (error) {
      showToast(error.response?.data?.message || error.message || "Unable to update favorites.");
    } finally {
      setSavingFavorite(false);
    }
  };

  // ============================================================
  // VISIT STORE
  // ============================================================
  const vendorSlug = product?.vendorId?.slug || vendor?.slug || null;
  const productSlug = product?.slug || null;
  const productNavigationState = fromVendorStore ? { fromVendorStore: true, vendorSlug } : undefined;
  const productUrl =
    vendorSlug && productSlug
      ? storeProductPath({ ...product?.vendorId, ...vendor, slug: vendorSlug }, productSlug)
      : "#";

  // ============================================================
  // RENDER
  // ============================================================
  return (
    <Card className="group relative flex h-full min-w-0 flex-col overflow-hidden border-0 bg-white rounded-lg transition-all duration-300 hover:-translate-y-1 hover:shadow-xl">
      {/* Toast */}
      {toastMessage && (
        <div className="absolute top-3 left-1/2 -translate-x-1/2 z-50">
          <div className="bg-gray-900 text-white text-xs font-medium px-4 py-2 rounded-full shadow-lg whitespace-nowrap">
            {toastMessage}
          </div>
        </div>
      )}

      {/* Image Section */}
      {productUrl !== "#" ? (
        <Link to={productUrl} state={productNavigationState} className="block relative">
          <div className="relative aspect-square sm:aspect-auto sm:h-52 overflow-hidden bg-gray-100">
            {imageUrl && !imageError ? (
              <img
                src={imageUrl}
                alt={product?.name || "Product"}
                className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                onError={() => setImageError(true)}
              />
            ) : (
              <div className="w-full h-full flex flex-col items-center justify-center text-gray-400">
                <ImageOff className="w-10 h-10 mb-2" />
                <span className="text-sm">No image available</span>
              </div>
            )}
            <div className="absolute inset-0 bg-gradient-to-t from-black/30 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />

            {discountBadges}
            {product?.isFeatured && (
              <div className="absolute top-1 right-1 sm:top-3 sm:right-3">
                <span className="inline-flex items-center rounded-full bg-yellow-400 px-1 sm:px-2.5 py-1 text-[8px] sm:text-[10px] font-bold text-yellow-900 shadow-md">
                  FEATURED
                </span>
              </div>
            )}
            {isProductHold && (
              <div className="absolute bottom-3 left-3">
                <span className="inline-flex items-center rounded-full bg-red-600 px-2.5 py-1 text-[10px] font-bold text-white shadow-md">
                  ON HOLD
                </span>
              </div>
            )}
            {images.length > 1 && (
              <div className="absolute bottom-3 right-3">
                <span className="rounded-full bg-black/70 backdrop-blur-sm px-2.5 py-1 text-xs font-medium text-white">
                  +{images.length - 1} photos
                </span>
              </div>
            )}
          </div>
        </Link>
      ) : (
        <div className="relative aspect-square sm:aspect-auto sm:h-52 overflow-hidden bg-gray-100">
          {imageUrl && !imageError ? (
            <img
              src={imageUrl}
              alt={product?.name || "Product"}
              className="w-full h-full object-cover"
              onError={() => setImageError(true)}
            />
          ) : (
            <div className="w-full h-full flex flex-col items-center justify-center text-gray-400">
              <ImageOff className="w-10 h-10 mb-2" />
              <span className="text-sm">No image available</span>
            </div>
          )}
          {discountBadges}
        </div>
      )}

      {/* Content */}
      <CardContent className="p-2 sm:p-3 flex flex-1 min-w-0 flex-col">
        {/* Vendor */}
        {vendor && !fromVendorStore && (
          <div className="flex items-center justify-between gap-1 sm:gap-2 mb-1.5">
            <div className="flex flex-1 items-center gap-1.5 min-w-0">
              <div className="flex h-6 w-6 shrink-0 items-center justify-center overflow-hidden rounded-full bg-gray-100">
                {vendor.logo && !vendorLogoError ? (
                  <img
                    src={vendor.logo}
                    alt=""
                    className="h-full w-full object-cover"
                    onError={() => setVendorLogoError(true)}
                  />
                ) : (
                  <span className="text-[9px] font-semibold text-gray-500" aria-hidden="true">
                    {vendor.storeName?.trim()?.charAt(0)?.toUpperCase() || "S"}
                  </span>
                )}
              </div>
              {vendorSlug ? (
                <Link
                  to={storePath({ ...product?.vendorId, ...vendor, slug: vendorSlug })}
                  aria-label={`Visit ${vendor.storeName || "store"}`}
                  className="truncate text-xs font-medium text-gray-600 underline decoration-gray-400 underline-offset-2 hover:text-primary hover:decoration-primary"
                >
                  {vendor.storeName || "Store"}
                </Link>
              ) : (
                <span className="truncate text-xs font-medium text-gray-600 underline decoration-gray-400 underline-offset-2">
                  {vendor.storeName || "Store"}
                </span>
              )}
            </div>
          </div>
        )}

        {/* Product Info */}
        <div className="flex-grow">
          {productUrl !== "#" ? (
            <Link to={productUrl} state={productNavigationState} className="block">
              <h3 className="font-semibold text-sm sm:text-base text-gray-700 font-['Arial'] line-clamp-2 leading-snug mb-1 transition-colors group-hover:text-primary">
                {product?.name || "Unnamed Product"}
              </h3>
            </Link>
          ) : (
            <h3 className="font-semibold text-sm sm:text-base text-gray-700 font-['Arial'] line-clamp-2 leading-snug mb-1">
              {product?.name || "Unnamed Product"}
            </h3>
          )}

          {/* Variant Selector */}
          {variants.length > 0 && (
            <div className="mb-2">
              <button
                type="button"
                onClick={toggleVariants}
                disabled={isLoading}
                aria-label={`Select variant: ${selectedVariant?.variantName || "Variant"}`}
                aria-expanded={showVariants && variants.length > 1}
                className="flex w-full min-w-0 items-center justify-between gap-2 text-left text-xs font-semibold text-green-800 hover:text-green-900 transition-colors"
              >
                <span className="truncate">
                  {selectedVariant?.variantName || "Variant"}
                </span>
                <ChevronDown
                  className={`w-3.5 h-3.5 flex-shrink-0 transition-transform duration-200 ${
                    showVariants ? "rotate-180" : ""
                  }`}
                />
              </button>

              {showVariants && variants.length > 1 && (
                <div className="mt-2 -mx-1">
                  <div className="flex gap-2 overflow-x-auto px-1 pb-1 snap-x snap-mandatory scrollbar-thin">
                    {variants.map((variant, idx) => {
                      const isSelected =
                        selectedVariant === variant ||
                        (selectedVariant?.variantName === variant.variantName &&
                          selectedVariant?.originalPrice ===
                            variant.originalPrice &&
                          selectedVariant?.discountedPrice ===
                            variant.discountedPrice);

                      const vOriginal = variant.originalPrice;
                      const vDiscounted = variant.discountedPrice;
                      const vHasDiscount =
                        vDiscounted !== null && vDiscounted < vOriginal;
                      const vPrice = vHasDiscount ? vDiscounted : vOriginal;

                      return (
                        <button
                          key={`${variant.variantName}-${idx}`}
                          type="button"
                          onClick={() => handleVariantChange(variant)}
                          disabled={isLoading}
                          className={`
                            relative flex items-center justify-between gap-1 sm:gap-3
                            min-w-[155px] max-w-[200px] h-11 flex-shrink-0 snap-start
                            rounded-lg border px-3 transition-all
                            ${
                              isSelected
                                ? "border-primary bg-primary/10 ring-1 ring-primary"
                                : "border-gray-200 bg-white hover:border-primary/50 hover:bg-gray-50"
                            }
                            ${isLoading ? "opacity-50 cursor-not-allowed" : ""}
                          `}
                        >
                          <span
                            className={`text-xs font-semibold truncate ${
                              isSelected ? "text-primary" : "text-gray-700"
                            }`}
                          >
                            {variant.variantName}
                          </span>
                          <div className="flex flex-col items-end">
                            {vHasDiscount && (
                              <span className="text-[9px] text-gray-400 line-through">
                                ₱{vOriginal.toFixed(2)}
                              </span>
                            )}
                            <span
                              className={`text-[11px] font-semibold whitespace-nowrap ${
                                isSelected ? "text-primary" : "text-gray-600"
                              }`}
                            >
                              ₱{vPrice.toFixed(2)}
                            </span>
                          </div>
                          {isSelected && (
                            <Check className="absolute -top-1.5 -right-1.5 w-4 h-4 rounded-full bg-primary text-white p-0.5" />
                          )}
                        </button>
                      );
                    })}
                  </div>
                  {variants.length > 2 && (
                    <p className="text-[9px] text-gray-400 text-center mt-1">
                      Swipe horizontally to view variants
                    </p>
                  )}
                </div>
              )}
            </div>
          )}

          <div className="mb-1.5 flex min-h-4 items-center justify-between gap-2">
            {/* Stock */}
            {stockAvailable !== null ? (
              isProductHold ? (
                <span className="inline-flex items-center gap-1.5 text-xs text-red-600">
                  <span className="w-1.5 h-1.5 rounded-full bg-red-500" />
                  Product on hold
                </span>
              ) : isOutOfStock ? (
                <span className="inline-flex items-center gap-1.5 text-xs text-red-600">
                  <span className="w-1.5 h-1.5 rounded-full bg-red-500" />
                  Out of stock
                </span>
              ) : (
                <span className="inline-flex items-center gap-1.5 text-xs text-green-600">
                  <span className="w-1.5 h-1.5 rounded-full bg-green-500" />
                  {stockAvailable <= 5
                    ? `Only ${stockAvailable} left`
                    : "In stock"}
                </span>
              )
            ) : <span />}
            {product?.preparationTime !== undefined &&
              product?.preparationTime !== null && (
                <span className="ml-auto inline-flex shrink-0 items-center gap-1 text-[10px] text-gray-500">
                  <Timer size={13} aria-hidden="true" />
                  {Number(product.preparationTime) || 0} min
                </span>
              )}
          </div>
        </div>

        {/* Price – uses selectedPrice state */}
        <div className="mb-2 flex min-w-0 items-center justify-between gap-2">
          <span
            className={`text-sm sm:text-lg ${hasDiscount ? "text-green-700 font-semibold font-['Arial']" : "text-gray-900"}`}
          >
            ₱{selectedPrice.toFixed(2)}
          </span>
          {canFavorite && (
            <button
              type="button"
              onClick={handleToggleFavorite}
              disabled={savingFavorite}
              aria-label={`${isFavorite ? "Remove" : "Add"} ${product?.name || "product"} ${isFavorite ? "from" : "to"} favorites`}
              aria-pressed={isFavorite}
              title={isFavorite ? "Remove from favorites" : "Add to favorites"}
              className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg text-green-900 transition-colors hover:bg-green-50 disabled:opacity-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-green-700"
            >
              <Heart
                size={19}
                aria-hidden="true"
                fill={isFavorite ? "currentColor" : "none"}
                className={isFavorite ? "text-green-600" : "text-green-900"}
              />
            </button>
          )}
        </div>

        {/* Add to Cart */}
        {!showQuantityInput || isUnavailable ? (
          <Button
            type="button"
            onClick={() => setShowQuantityInput(true)}
            aria-label={unavailableLabel || 'Add to cart'}
            title={unavailableLabel || 'Add to cart'}
            disabled={
              storeClosed || isUnavailable ||
              isLoading ||
              (variants.length > 0 && !selectedVariant) ||
              !selectedPrice ||
              selectedPrice <= 0
            }
            className="-mx-1 w-[calc(100%+0.5rem)] min-h-10 h-auto px-1 rounded-lg gap-1 whitespace-normal text-xs sm:text-sm font-semibold transition-all bg-red-600 text-white hover:bg-red-700 focus-visible:ring-red-600"
          >
            <ShoppingCart className="h-4 w-4 shrink-0" aria-hidden="true" />
            {unavailableLabel || 'Add'}
          </Button>
        ) : (
          <div className="-mx-1 flex flex-nowrap items-center gap-1">
            {/* Quantity controls */}
            <div className="flex items-center justify-center h-10 shrink-0 rounded-lg border border-gray-200 overflow-hidden bg-gray-50">
              <button
                type="button"
                onClick={handleDecreaseQuantity}
                disabled={quantity <= 1 || isLoading}
                className="h-full w-6 sm:w-8 flex items-center justify-center text-gray-600 hover:bg-gray-200 disabled:opacity-40 transition-colors"
                aria-label="Decrease quantity"
              >
                <Minus className="w-3.5 h-3.5" />
              </button>
              <input
                type="number"
                value={quantity}
                onChange={handleQuantityInput}
                min="1"
                max={maxQuantity}
                disabled={isLoading}
                aria-label="Quantity"
                className="w-7 sm:w-9 h-full text-center text-xs sm:text-sm font-semibold bg-transparent border-0 focus:outline-none focus:ring-0"
              />
              <button
                type="button"
                onClick={handleIncreaseQuantity}
                disabled={quantity >= maxQuantity || isLoading}
                className="h-full w-6 sm:w-8 flex items-center justify-center text-gray-600 hover:bg-gray-200 disabled:opacity-40 transition-colors"
                aria-label="Increase quantity"
              >
                <Plus className="w-3.5 h-3.5" />
              </button>
            </div>

            <Button
              type="button"
              onClick={handleAddToCart}
              aria-label={isLoading ? 'Adding to cart' : 'Add to cart'}
              title={isLoading ? 'Adding to cart' : 'Add to cart'}
              disabled={
                isLoading ||
                isUnavailable ||
                !selectedPrice ||
                selectedPrice <= 0
              }
              className="min-w-0 flex-1 h-10 rounded-lg gap-1 px-1 text-[10px] sm:text-sm font-semibold bg-red-600 text-white hover:bg-red-700 focus-visible:ring-red-600"
            >
              {isLoading ? 'Adding...' : 'Add'}
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default ProductCard;
